import * as React from "react";
import { z } from "zod";
import { useForm, FormProvider, useFormState } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../../../components/ui/button";
import { Icons } from "../../../utils/icons";
import { useToast } from "../../../components/ui/use-toast";
import SectionFields401k from "../components/401k/section-fields"; // Import form fields component
import XanoAPI from "../../../lib/XanoAPI";
import { ClientType } from "../../../types";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";
import { Clock, Sparkles } from "lucide-react";

// Define the schema for the 401k form
const financialInformation401kSchema = z.object({
  t_401k_type: z.enum(["Traditional", "Roth"]), // Dropdown with "Traditional" or "Roth"
  t_401k_currentSalary: z.number().min(0, "Current salary must be positive"), // Annual salary
  t_401k_payPeriodsPerYear: z.number().min(1, "Must be at least 1"), // Pay periods per year
  t_401k_monthlyContributionPercent: z
    .number()
    .min(0)
    .max(100, "Percentage must be between 0 and 100"), // % of income to contribute
  t_401k_employerMatchPercent: z
    .number()
    .min(0)
    .max(100, "Percentage must be between 0 and 100"), // Employer's monthly % match
  t_401k_salaryLimitForEmployerMatch: z
    .number()
    .min(0)
    .max(100, "Percentage must be between 0 and 100"), // Salary limit for employer match
  t_401k_annualManagementFee: z
    .number()
    .min(0)
    .max(100, "Percentage must be between 0 and 100"), // Annual management fee
  t_401k_selfDirected: z.boolean(), // Self-Directed Slider (Yes/No)
  t_401k_estimatedEarlyWithdrawals: z.number().min(0, "Must be positive"), // Est. Early Withdrawals
  t_401k_earlyWithdrawalPenalty: z.boolean(), // Early Withdrawals Subject to Penalty (Yes/No)
  t_401k_existing401kValue: z.number(),
});

type FormData401k = z.infer<typeof financialInformation401kSchema>;

const FinancialInformation401k = ({
  client,
  onUpdate,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  handleTabChange,
  formsDirty,
  saveStatus,
  lastSaveTime,
  isSmartPasteOpen,
  setIsSmartPasteOpen,
  setSmartPasteHandlers,
}: {
  client: ClientType;
  onUpdate: Function;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  handleTabChange: Function;
  formsDirty: any;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
  isSmartPasteOpen?: boolean;
  setIsSmartPasteOpen?: (open: boolean) => void;
  setSmartPasteHandlers?: (handlers: any) => void;
}) => {
  const { toast } = useToast();
  const api = XanoAPI.getInstance();
  const isAutoSaving = React.useRef(false);
  
  // Smart Paste operation flag - prevents form reset during population
  const isSmartPastePopulating = React.useRef(false);
  
  // Refs for handler functions to avoid stale closures
  const handlersRef = React.useRef({
    onPersonalDataParsed: (data: Partial<ClientType>) => {},
    onFinancialDataParsed: (data: Partial<ClientType>) => {},
    onPopulateSingleField: (fieldName: string, value: any) => {},
    onSave: async () => {},
  });

  // Initialize react-hook-form with defaultValues
  const form = useForm<FormData401k>({
    resolver: zodResolver(financialInformation401kSchema),
    mode: "onChange", // Enable real-time dirty tracking
    criteriaMode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      t_401k_type: client?.t_401k_type || "Traditional",
      t_401k_currentSalary: client?.t_401k_currentSalary || 0,
      t_401k_payPeriodsPerYear: client?.t_401k_payPeriodsPerYear || 12,
      t_401k_monthlyContributionPercent:
        client?.t_401k_monthlyContributionPercent || 0,
      t_401k_employerMatchPercent: client?.t_401k_employerMatchPercent || 0,
      t_401k_salaryLimitForEmployerMatch:
        client?.t_401k_salaryLimitForEmployerMatch || 0,
      t_401k_annualManagementFee: client?.t_401k_annualManagementFee || 0,
      t_401k_selfDirected: client?.t_401k_selfDirected || false,
      t_401k_estimatedEarlyWithdrawals:
        client?.t_401k_estimatedEarlyWithdrawals || 0,
      t_401k_earlyWithdrawalPenalty:
        client?.t_401k_earlyWithdrawalPenalty || false,
      t_401k_existing401kValue: client?.t_401k_existing401kValue || 0.0,
    },
  });

  const { handleSubmit, control, reset, formState, trigger, setValue } = form;

  // Access formState properties directly for reactivity
  const { isValid, isDirty, dirtyFields } = formState;

  // Register Smart Paste handlers with parent (must be at top level, not conditional)
  React.useEffect(() => {
    if (setSmartPasteHandlers) {
      console.log('📝 [401k Tab] Registering Smart Paste handlers');
      setSmartPasteHandlers((prev: any) => ({
        ...prev,
        financial: {
          onPersonalDataParsed: (data: any) => {
            console.log('📞 [401k Tab] onPersonalDataParsed wrapper called');
            return handlersRef.current.onPersonalDataParsed(data);
          },
          onFinancialDataParsed: (data: any) => {
            console.log('📞 [401k Tab] onFinancialDataParsed wrapper called');
            return handlersRef.current.onFinancialDataParsed(data);
          },
          onPopulateSingleField: (fieldName: string, value: any) => {
            console.log('📞 [401k Tab] onPopulateSingleField wrapper called');
            return handlersRef.current.onPopulateSingleField(fieldName, value);
          },
          onSave: () => {
            console.log('📞 [401k Tab] onSave wrapper called');
            return handlersRef.current.onSave();
          },
          isSaveDisabled: !isValid || !isDirty,
        },
      }));
      console.log('✅ [401k Tab] Handlers registered successfully');
    }
  }, [setSmartPasteHandlers, isValid, isDirty]);  // Include isValid/isDirty to update isSaveDisabled

  // Reset form when client changes
  React.useEffect(() => {
    if (client?.id && !isSmartPastePopulating.current) {
      reset({
        t_401k_type: client?.t_401k_type || "Traditional",
        t_401k_currentSalary: client?.t_401k_currentSalary || 0,
        t_401k_payPeriodsPerYear: client?.t_401k_payPeriodsPerYear || 12,
        t_401k_monthlyContributionPercent:
          client?.t_401k_monthlyContributionPercent || 0,
        t_401k_employerMatchPercent: client?.t_401k_employerMatchPercent || 0,
        t_401k_salaryLimitForEmployerMatch:
          client?.t_401k_salaryLimitForEmployerMatch || 0,
        t_401k_annualManagementFee: client?.t_401k_annualManagementFee || 0,
        t_401k_selfDirected: client?.t_401k_selfDirected || false,
        t_401k_estimatedEarlyWithdrawals:
          client?.t_401k_estimatedEarlyWithdrawals || 0,
        t_401k_earlyWithdrawalPenalty:
          client?.t_401k_earlyWithdrawalPenalty || false,
        t_401k_existing401kValue: client?.t_401k_existing401kValue || 0.0,
      });
      setTimeout(() => trigger(), 0);
    }
  }, [client?.id, reset, trigger]);

  // Sync React Hook Form's isDirty state with parent's formsDirty state
  React.useEffect(() => {
    if (isDirty && dirtyFields) {
      const dirtyFieldNames = Object.keys(dirtyFields);
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: dirtyFieldNames,
      }));
    } else {
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: [],
      }));
    }
  }, [isDirty, dirtyFields]);

  const onSubmit = async (data: FormData401k) => {
    if (!client || !client.id) {
      console.error("Client ID is missing.");
      return;
    }

    try {
      const processedData = {
        ...client,
        ...data,
      };

      await api.updateClient(client.id, processedData);
      await fetchClientData(client.id);
      reset(data);
      resetFormsDirtyState();

      // Only show toast notification on manual save, not auto-save
      if (!isAutoSaving.current) {
        toast({
          title: "401k Information Updated!",
          description:
            "Your client's 401k financial information has been updated.",
        });
      }
      
      // Reset the auto-save flag
      isAutoSaving.current = false;
    } catch (error) {
      console.error("Error updating 401k information:", error);
    }
  };

  // Handle single field population from Smart Paste
  const handlePopulateSingleField = (fieldName: string, value: any) => {
    isSmartPastePopulating.current = true;
    
    try {
      // Get current form values for calculations
      const currentSalary = form.getValues('t_401k_currentSalary') || client?.t_401k_currentSalary || 0;
      const payPeriodsPerYear = form.getValues('t_401k_payPeriodsPerYear') || client?.t_401k_payPeriodsPerYear || 26;
      
      let targetFieldName = fieldName;
      let targetValue = value;
      
      // Handle contributionPerPay - convert to percentage (flexible matching)
      const normalizedFieldName = fieldName.toLowerCase().replace(/[_\s]/g, '');
      if (normalizedFieldName.includes('contributionperpay') || normalizedFieldName.includes('contributionperpayperiod')) {
        const contributionPerPay = typeof value === 'number' ? value : parseFloat(String(value));
        if (!isNaN(contributionPerPay) && currentSalary > 0 && payPeriodsPerYear > 0) {
          const annualContribution = contributionPerPay * payPeriodsPerYear;
          const percentage = (annualContribution / currentSalary) * 100;
          targetFieldName = 't_401k_monthlyContributionPercent';
          targetValue = percentage;
          console.log(`   🔄 Converting contributionPerPay (${contributionPerPay}) → percentage (${percentage.toFixed(2)}%)`);
        } else {
          console.warn(`   ⚠️ Cannot convert contributionPerPay: salary=${currentSalary}, payPeriods=${payPeriodsPerYear}`);
          return; // Skip if we can't convert
        }
      }
      
      // Handle annualContribution - convert to percentage (flexible matching)
      if (normalizedFieldName.includes('annualcontribution') && !normalizedFieldName.includes('percent')) {
        const annualContribution = typeof value === 'number' ? value : parseFloat(String(value));
        if (!isNaN(annualContribution) && currentSalary > 0) {
          const percentage = (annualContribution / currentSalary) * 100;
          targetFieldName = 't_401k_monthlyContributionPercent';
          targetValue = percentage;
          console.log(`   🔄 Converting annualContribution (${annualContribution}) → percentage (${percentage.toFixed(2)}%)`);
        } else {
          console.warn(`   ⚠️ Cannot convert annualContribution: salary=${currentSalary}`);
          return; // Skip if we can't convert
        }
      }
      
      // Map field name if needed
      const fieldMapping: Record<string, string> = {
        'grossIncome': 't_401k_currentSalary',
        'annualGrossIncome': 't_401k_currentSalary',
        'salary': 't_401k_currentSalary',
        'currentSalary': 't_401k_currentSalary',
        'payPeriodsAnnual': 't_401k_payPeriodsPerYear',
        'payPeriodsPerYear': 't_401k_payPeriodsPerYear',
        'payPeriods': 't_401k_payPeriodsPerYear',
        '401kContributionPercentage': 't_401k_monthlyContributionPercent',
        'contributionPercentage': 't_401k_monthlyContributionPercent',
        'monthlyContributionPercent': 't_401k_monthlyContributionPercent',
        '401kContributionRate': 't_401k_monthlyContributionPercent',
        'contributionRate': 't_401k_monthlyContributionPercent',
        'employerMatchPercent': 't_401k_employerMatchPercent',
        'employerMatch': 't_401k_employerMatchPercent',
        'companyMatch': 't_401k_employerMatchPercent',
        'companyMatchPercent': 't_401k_employerMatchPercent',
        '401kBalance': 't_401k_existing401kValue',
        'existing401kValue': 't_401k_existing401kValue',
        'current401kBalance': 't_401k_existing401kValue',
        '401kCurrentBalance': 't_401k_existing401kValue',
        'annualManagementFee': 't_401k_annualManagementFee',
        'managementFee': 't_401k_annualManagementFee',
        '401kManagementFee': 't_401k_annualManagementFee',
      };
      
      if (!targetFieldName.startsWith('t_401k_') && fieldMapping[fieldName]) {
        targetFieldName = fieldMapping[fieldName];
        console.log(`   🔄 Mapped ${fieldName} → ${targetFieldName}`);
      }
      
      // Only set if it's a valid 401k field
      if (targetFieldName.startsWith('t_401k_')) {
        setValue(targetFieldName as any, targetValue, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
        
        setFormsDirty((prev: any) => ({
          ...prev,
          financialInformation: [...(prev.financialInformation || []), targetFieldName],
        }));
        
        console.log(`✅ 401k field applied: ${targetFieldName} = ${targetValue}`);
      } else {
        console.warn(`⚠️ Skipping field ${fieldName} - not a valid 401k field`);
      }
    } catch (err) {
      console.error(`Failed to apply 401k field ${fieldName}:`, err);
    }
    
    setTimeout(() => {
      isSmartPastePopulating.current = false;
    }, 100);
  };
  
  handlersRef.current.onPopulateSingleField = handlePopulateSingleField;

  // Handle Smart Paste - Financial Information
  const handleFinancialDataParsed = (parsedData: Partial<ClientType>) => {
    console.log("🤖 [401k Tab] Smart Paste: Populating", Object.keys(parsedData).length, "financial fields");
    console.log("   All keys:", Object.keys(parsedData));

    // Remove fields that require specific JSON structure
    const fieldsToRemove = ['deductions', 'additionalIncome', 'allocations', 'federal_tsp_allocations', 'unused_sick_leave'];
    fieldsToRemove.forEach(field => delete parsedData[field as keyof ClientType]);

    // Map common field names to 401k-specific field names
    const fieldMapping: Record<string, string> = {
      // Salary and pay periods
      'grossIncome': 't_401k_currentSalary',
      'annualGrossIncome': 't_401k_currentSalary',
      'salary': 't_401k_currentSalary',
      'currentSalary': 't_401k_currentSalary',
      'payPeriodsAnnual': 't_401k_payPeriodsPerYear',
      'payPeriodsPerYear': 't_401k_payPeriodsPerYear',
      'payPeriods': 't_401k_payPeriodsPerYear',
      
      // Contribution fields (percentages)
      '401kContributionPercentage': 't_401k_monthlyContributionPercent',
      'contributionPercentage': 't_401k_monthlyContributionPercent',
      'monthlyContributionPercent': 't_401k_monthlyContributionPercent',
      '401kContributionRate': 't_401k_monthlyContributionPercent',
      'contributionRate': 't_401k_monthlyContributionPercent',
      
      // Employer match
      'employerMatchPercent': 't_401k_employerMatchPercent',
      'employerMatch': 't_401k_employerMatchPercent',
      'companyMatch': 't_401k_employerMatchPercent',
      'companyMatchPercent': 't_401k_employerMatchPercent',
      
      // Existing balance
      '401kBalance': 't_401k_existing401kValue',
      'existing401kValue': 't_401k_existing401kValue',
      'current401kBalance': 't_401k_existing401kValue',
      '401kCurrentBalance': 't_401k_existing401kValue',
      
      // Management fee
      'annualManagementFee': 't_401k_annualManagementFee',
      'managementFee': 't_401k_annualManagementFee',
      '401kManagementFee': 't_401k_annualManagementFee',
    };

    // Create mapped data object
    const mappedData: Partial<ClientType> = {};
    const fieldsToSet = new Set<string>();

    // Get salary and pay periods for contribution calculations
    const annualSalary = (parsedData.grossIncome as number | undefined) 
      || (parsedData as any).annualGrossIncome 
      || (parsedData as any).salary 
      || (parsedData as any).currentSalary 
      || parsedData.t_401k_currentSalary 
      || client?.t_401k_currentSalary 
      || 0;
    const payPeriodsPerYear = (parsedData.payPeriodsAnnual as number | undefined)
      || (parsedData as any).payPeriodsPerYear
      || (parsedData as any).payPeriods
      || parsedData.t_401k_payPeriodsPerYear
      || client?.t_401k_payPeriodsPerYear
      || 26;

    Object.entries(parsedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle contributionPerPay - convert to percentage (flexible matching)
        const normalizedKey = key.toLowerCase().replace(/[_\s]/g, '');
        if (normalizedKey.includes('contributionperpay') || normalizedKey.includes('contributionperpayperiod')) {
          const contributionPerPay = typeof value === 'number' ? value : parseFloat(String(value));
          if (!isNaN(contributionPerPay) && annualSalary > 0 && payPeriodsPerYear > 0) {
            // Calculate percentage: (contributionPerPay * payPeriodsPerYear) / annualSalary * 100
            const annualContribution = contributionPerPay * payPeriodsPerYear;
            const percentage = (annualContribution / annualSalary) * 100;
            mappedData.t_401k_monthlyContributionPercent = percentage;
            fieldsToSet.add('t_401k_monthlyContributionPercent');
            console.log(`   🔄 Converted ${key} (${contributionPerPay} per pay) → t_401k_monthlyContributionPercent = ${percentage.toFixed(2)}%`);
          }
          return; // Skip adding this field directly
        }
        
        // Handle annualContribution - convert to percentage (flexible matching)
        if (normalizedKey.includes('annualcontribution') && !normalizedKey.includes('percent')) {
          const annualContribution = typeof value === 'number' ? value : parseFloat(String(value));
          if (!isNaN(annualContribution) && annualSalary > 0) {
            // Calculate percentage: (annualContribution / annualSalary) * 100
            const percentage = (annualContribution / annualSalary) * 100;
            mappedData.t_401k_monthlyContributionPercent = percentage;
            fieldsToSet.add('t_401k_monthlyContributionPercent');
            console.log(`   🔄 Converted ${key} (${annualContribution} annually) → t_401k_monthlyContributionPercent = ${percentage.toFixed(2)}%`);
          }
          return; // Skip adding this field directly
        }
        
        // Check if this field needs mapping
        const mappedKey = fieldMapping[key] || key;
        
        // Only map if it's a 401k field or already a valid 401k field
        if (mappedKey.startsWith('t_401k_') || key.startsWith('t_401k_')) {
          mappedData[mappedKey as keyof ClientType] = value;
          fieldsToSet.add(mappedKey);
          if (mappedKey !== key) {
            console.log(`   🔄 Mapped ${key} → ${mappedKey} = ${value}`);
          }
        } else {
          // Try direct mapping if it's already a valid form field
          mappedData[key as keyof ClientType] = value;
          fieldsToSet.add(key);
        }
      }
    });

    isSmartPastePopulating.current = true;
    
    // Populate mapped fields
    Object.entries(mappedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && fieldsToSet.has(key)) {
        try {
          setValue(key as any, value, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
          console.log(`   ✅ Set ${key} = ${value}`);
        } catch (err) {
          console.error(`   ❌ Failed to set ${key}:`, err);
        }
      }
    });
    
    setTimeout(() => {
      trigger();
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: Array.from(fieldsToSet),
      }));
      isSmartPastePopulating.current = false;
      console.log("✅ [401k Tab] Smart Paste: Financial population complete");
      console.log("   Fields populated:", Array.from(fieldsToSet));
    }, 100);
  };
  
  handlersRef.current.onFinancialDataParsed = handleFinancialDataParsed;
  handlersRef.current.onPersonalDataParsed = () => {
    console.log("⚠️ Personal data parsed in 401k tab - ignored");
  };
  handlersRef.current.onSave = async () => {
    console.log("💾 Smart Paste Save triggered (401k)");
    return handleSubmit(onSubmit)();
  };

  // Real-time auto-save text with live updates
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  
  // Update current time every second for real-time display
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getAutoSaveText = () => {
    if (lastSaveTime === 0) return null;
    
    const timeSinceLastSave = currentTime - lastSaveTime;
    const seconds = Math.floor(timeSinceLastSave / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `Autosaved ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (seconds > 5) {
      return `Autosaved ${seconds} seconds ago`;
    } else if (seconds > 0) {
      return "Autosaved just now";
    }
    return null;
  };

  return (
    <div className="py-6 mb-8">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SectionFields401k client={client} />
          <div className="fixed bottom-0 right-0 m-3 z-50 flex items-center gap-3">
            {getAutoSaveText() && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-md border border-gray-200">
                <Clock className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs text-gray-600">
                  {getAutoSaveText()}
                </span>
              </div>
            )}
            {setIsSmartPasteOpen && (
              <Button
                type="button"
                variant="outline"
                className="p-3"
                onClick={() => setIsSmartPasteOpen(true)}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Smart Paste
              </Button>
            )}
            <Button
              type="submit"
              className="p-3 bg-accent"
              id="save-financial-information"
              disabled={!isValid || !isDirty}
              onClick={(e) => {
                // Check if this is a manual click (has isTrusted property)
                if (e.isTrusted) {
                  isAutoSaving.current = false;
                } else {
                  // Programmatic click (auto-save)
                  isAutoSaving.current = true;
                }
              }}
            >
              <Icons.save className="mr-2" />
              {isDirty ? "Save Changes" : "No Changes"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default FinancialInformation401k;
